import React, { useEffect, useState, useMemo } from 'react';
import {
  Title, Button, Icon, Text, Spacer
} from 'components/atoms';
import { ModalConfirm } from 'components/molecules';
import { HistoryCommitList } from 'components/organisms';
import styled, { css } from 'styled-components/macro';
import { useRouterContext } from 'hooks';
import { useEditorContext, actions, status } from 'context/EditorContext';
import { generateEditorPath, updatePageLastModified } from 'services/generators';
import { getVersions } from 'services/requests'
import { useDispatch } from 'react-redux'
import { saveDraft } from 'redux/draftsSlice'
import { toast } from 'react-toastify';
import { Toast, PageVersionModal } from 'containers'
import { PORTAL_WWW, rootSubwebs } from 'services/constants';

const computeDescription = (name, status) => {
  const firstName = typeof name === 'string' ? `${name.trim().split(' ')[0]} ` : null
  let statusMessage = 'Sem informação'
  if (status === 'actual' || status === 'history') {
    if (firstName) statusMessage = `${firstName} publicou a página`
    else statusMessage = 'Página publicada'
  }
  if (status === 'draft') {
    if (firstName) statusMessage = `${firstName} gravou a página`
    else statusMessage = 'Página gravada'
  }
  if (status === 'preview') {
    if (firstName) statusMessage = `${firstName} gerou uma pré-visualização`
    else statusMessage = 'Pré-visualização gerada'
  }
  return statusMessage
}

const computeStatus = (versionStatus) => {
  switch (versionStatus) {
  case 'draft':
    return status.edited
  case 'history':
    return 'history'
  case 'actual':
    return status.published
  case 'preview':
    return status.prePublished
  default:
    return 'nothing'
  }
}

const generatePageWithUpdatedSEO = (page) => page.map(module => {
  if (!module.name === 'SEO') return module
  return {
    ...module,
    lastModified: new Date().toISOString(),
  }
})

const parseVersionDataToProps = (data, lang, subweb) => {
  
  const subwebPath = rootSubwebs.includes(subweb) ? '' : `/${subweb}`
  const pageUrl = `${PORTAL_WWW}/${lang}${subwebPath}/preview/${data.slug}`


  return {
    id: data.id,
    date: data.date,
    user: {
      name: data.user,
      image: null,
    },
    actions: [
      {
        status: computeStatus(data.status),
        description: computeDescription(data.user, data.status),
        ...(data.slug ? { 
          link: pageUrl,
          linkExpired: data.isExpired 
        } : null),
      }
    ]
  }
}
  

const EditorModule = () => {
  const {
    match: {
      params: {
        lang,
        pageId,
        subweb
      },
    },
    history,
  } = useRouterContext();
  const [{ pageStatus }, dispatch] = useEditorContext();
  const dispatchRedux = useDispatch()
  const [commits, setCommits] = useState([])
  const [isLoading, setLoading] = useState(false)
  // eslint-disable-next-line no-unused-vars
  const [error, setError] = useState(null)
  const [confirmationChangeVersion, setConfirmationChangeVersion] = useState(
    {
      isOpen: false,
      page: null,
    }
  )
  const [previewVersion, setPreviewVersion] = useState(
    {
      isOpen: false,
      page: null,
      versionId: null,
      subweb: null,
      pageId: null,
      statusId: null,
      commit: null,
    }
  )

  const closeModule = () => {
    dispatch({
      type: actions.SET_MODULE_EDITED,
      data: {
        key: null,
        name: null,
      },
    });
    history.push(generateEditorPath({ lang, pageId, subweb }))
  }

  useEffect(() => {
    async function fetchVersions() {
      if (pageId && lang && subweb) {
        try {
          setLoading(true)
          const { data: { data } } = await getVersions({ pageId, lang, subweb })
          setCommits(data)
          setLoading(false)
          setError(null)
        } catch (err) {
          setLoading(false)
          setCommits([])
          setError(err.message)
        }
      }
    }
    fetchVersions()
  }, [pageId, lang, subweb, pageStatus])

  /*
    When user sets the current editor from a history version
    it checks if theres any draft and warns the user it will be deleted
    it also updates the version lastModified property to prevent
    the outdated version modal from popping up
  */

  const handleConfirmChangeVersion = (shouldChangeVersion) => {
    const { page } = confirmationChangeVersion
    if (shouldChangeVersion && page && page.length > 0) {
      dispatch({
        type: actions.PAGE_DATA_UPDATE,
        data: generatePageWithUpdatedSEO(page),
      });
      dispatchRedux(saveDraft({ pageId, content: generatePageWithUpdatedSEO(page), lang }))
      toast.success(
        <Toast
          type="success"
          title="Versão seleccionada ativada"
          description="Grave e publique a página para publicar esta versão"
        />
      );
      setConfirmationChangeVersion({
        isOpen: false,
        page: null,
      })
    } else {
      // reset confirmation modal
      setConfirmationChangeVersion({
        isOpen: false,
        page: null,
      })
    }
  }

  const handleContact = (email) => {
    window.location.href = `mailto:${email}`;
  }

  const setVersion = (pageData) => {
    /*
      check if there is unsaved draft
      when theres a draft it shows a confirmation modal
      warning the user that his draft will be deleted
     */
    setPreviewVersion({
      isOpen: false,
      page: null,
      versionId: null,
      subweb: null,
      pageId: null,
      statusId: null,
      commit: null,
    })
    if (pageStatus === status.local) {
      setConfirmationChangeVersion({
        isOpen: true,
        page: pageData,
      })
    } else {
      // update lastModified at SEO to prevent isOutdated modal warning
      const pageUpdated = updatePageLastModified(pageData)
      dispatch({
        type: actions.PAGE_DATA_UPDATE,
        data: pageUpdated,
      });
      dispatchRedux(saveDraft({ pageId, content: pageData, lang }))
      toast.success(
        <Toast
          type="success"
          title="Versão seleccionada ativada"
          description="Grave e publique a página para publicar esta versão"
        />
      );
    }
  }

  const handlePreviewVersion = async ({ versionId, statusId, commit }) => {
    setPreviewVersion({
      pageId,
      subweb,
      versionId,
      statusId,
      commit,
      isOpen: true,
    })
  }

  // eslint-disable-next-line no-unused-vars
  const commitsComputed = useMemo(() => {
    if (commits && commits.length > 0) {
      return commits.map(commit => ({
        ...parseVersionDataToProps(commit, lang, subweb),
        onPreview: () => handlePreviewVersion({
          versionId: commit.id,
          statusId: commit.statusId,
          commit: parseVersionDataToProps(commit, lang, subweb)
        }),
        onContact: typeof commit.userUu === 'string' ? () => handleContact(commit.userUu) : null,
      }))
    }
    return []
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [commits, pageStatus])

  const renderPopups = () => (
    <>
      <ModalConfirm
        isOpen={confirmationChangeVersion.isOpen}
        toggle={() => setConfirmationChangeVersion({
          ...confirmationChangeVersion,
          isOpen: !confirmationChangeVersion.isOpen
        })}
        onChange={handleConfirmChangeVersion}
        title="Tem alterações por gravar"
        message="Irá perder as suas edições caso deseje restaurar a versão, deseja continuar?"
        textConfirm="Restaurar"
        textDiscard="Cancelar"
      />
      <PageVersionModal
        pageId={previewVersion.pageId}
        subweb={previewVersion.subweb}
        versionId={previewVersion.versionId}
        statusId={previewVersion.statusId}
        isOpen={previewVersion.isOpen}
        commit={previewVersion.commit}
        toggle={() => setPreviewVersion({
          ...previewVersion,
          isOpen: !previewVersion.isOpen
        })}
        onSetVersion={setVersion}
      />
    </>
  )

  return (
    <>
      {renderPopups()}
      <Wrapper>
        <Top>
          <Top.Header>
            <Title modifiers={['fixMarginBottom']}>Histórico</Title>
            <Button modifiers={['invisibleDarker']} onClick={closeModule}>
              <Icon icon="close" />
            </Button>
          </Top.Header>
          <Spacer top="xs" />
          <Text modifiers={['content']}>
            Todas as versões publicadas da página.
          </Text>
        </Top>
        <End>
          {isLoading
            ? (
              <HistoryCommitList isLoading />
            )
            : (
              <HistoryCommitList
                list={commitsComputed}
              />
            )
          }
        </End>
      </Wrapper>
    </>
  );
};

const Wrapper = styled.div(props => css`
  padding-bottom:  ${props.theme.spacing.xl3};
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  background-color: ${props => props.theme.colors.bgLighterS};
  overflow-y: auto;
  max-height: calc(100vh - 90px);
`)

const Top = styled.div`
  display: flex;
  flex-direction: column;
  padding-top: ${props => props.theme.spacing.xl};
  padding-bottom: ${props => props.theme.spacing.m};
  padding-right: ${props => props.theme.spacing.xl};
  padding-left: ${props => props.theme.spacing.xl3}
`;

Top.Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`

const End = styled.div`
  padding-bottom: ${props => props.theme.spacing.m};
  padding-right: ${props => props.theme.spacing.xl};
  padding-left: ${props => props.theme.spacing.xl3};
`

export default EditorModule;
